import { Dispatch, SetStateAction, useCallback, useState } from "react";

type ArrayHook<T> = {
  value: T[];
  add: (item: T) => void;
  removeAt: (index: number) => void;
  removeAtMany: (indexes: number[]) => void;
  removeIf: (predicate: (item: T) => boolean) => void;
  updateAt: (index: number, updateFn: (current: T) => T) => void;
  updateIf: (updateFn: (item: T) => T | null) => void;
  clear: () => void;
};

export type UseArraySetter<T> = Dispatch<SetStateAction<T[]>>;

export const useArray = <T>(
  value: T[],
  setValue: UseArraySetter<T>,
): ArrayHook<T> => {
  const add = useCallback(
    (item: T) => {
      setValue((prev) => [...prev, item]);
    },
    [setValue],
  );

  const removeAt = useCallback(
    (index: number) => {
      setValue((prev) => prev.filter((_, i) => i !== index));
    },
    [setValue],
  );

  const removeAtMany = useCallback(
    (indexes: number[]) => {
      setValue((prev) => prev.filter((_, i) => !indexes.includes(i)));
    },
    [setValue],
  );

  const removeIf = useCallback(
    (predicate: (item: T) => boolean) => {
      setValue((prev) => prev.filter((item) => !predicate(item)));
    },
    [setValue],
  );

  const updateAt = useCallback(
    (index: number, updateFn: (current: T) => T) => {
      setValue((prev) => {
        const newArray = prev.slice();
        newArray[index] = updateFn(newArray[index]);
        return newArray;
      });
    },
    [setValue],
  );

  const updateIf = useCallback(
    (updateFn: (item: T) => T | null) => {
      setValue((prev) => prev.map((item) => updateFn(item) ?? item));
    },
    [setValue],
  );

  const clear = useCallback(() => {
    setValue([]);
  }, [setValue]);

  return {
    value,
    add,
    removeAt,
    removeAtMany,
    removeIf,
    updateAt,
    updateIf,
    clear,
  };
};

export const useArrayState = <T>(
  initialValue: T[] | (() => T[]) = [],
): ArrayHook<T> => {
  const [value, setValue] = useState(initialValue);
  return useArray<T>(value, setValue);
};
